import { Component, inject, ViewChild } from '@angular/core';
import {
  AnalisisClinico,
  Atencion,
  AtencionDashboard,
  Paciente,
  SignosVitales,
} from '../pacientes/pacientes';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DATE_LOCALE, MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { Medicamentos } from './medicamentos/medicamentos';
import Swal from 'sweetalert2';
import { RecetasPdf } from '../../core/services/recetas-pdf.service';
import { Router, RouterLink } from '@angular/router';
import { EstadoAtencionService } from '../../core/services/estado-anterior.service';
import { MatTableModule } from '@angular/material/table';
import { AnalisisClinicoDialog } from './analisis-clinico/analisis-clinico-dialog/analisis-clinico-dialog';
import { AtencionService } from '../../core/services/atencion.service';
import { EstudiosService } from '../../core/services/estudios.service';
import { EstudiosSolicitadosDialog } from './estudios-solicitados-dialog/estudios-solicitados-dialog';
import { MedicamentosService } from '../../core/services/medicamentos.service';
import { ArchivosAtencionService } from '../../core/services/archivos-atencion.service';
import { IndicacionService } from '../../core/services/indicacion.service';
import { RecetasService } from '../../core/services/recetas.service';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PacienteService } from '../../core/services/pacientes.service';
import { CAMPOS_EXPEDIENTE, VALOR_EXPEDIENTE_VACIO } from '../../core/constants/expendiente';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ResumenRecetaDialog } from '../estudios/resumen-receta-dialog/resumen-receta-dialog';
import { ResumenEstudiosDialog } from '../estudios/resumen-estudios-dialog/resumen-estudios-dialog';
import { HistorialSignosVitalesDialog } from './historial-signos-vitales-dialog/historial-signos-vitales-dialog';
import {
  GRUPOS_HISTORIA,
  HistoriaClinicaDialog,
} from './historia-clinica-dialog/historia-clinica-dialog';
import { GraficasSignosVitales } from '../pacientes/registro-signos-vitales/graficas-signos-vitales/graficas-signos-vitales';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-consultas',
  imports: [
    MatIcon,
    DatePipe,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatIconButton,
  ],
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: 'es-MX' }],
  templateUrl: './consultas.html',
  styleUrl: './consultas.scss',
})
export class Consultas {
  constructor(
    private router: Router,
    private authService: AuthService,
    private dialog: MatDialog,
    private atencionService: AtencionService,
    private estudiosService: EstudiosService,
    private recetaPdf: RecetasPdf,
    private estadoAtencionService: EstadoAtencionService,
    private archivosAtencionService: ArchivosAtencionService,
    private medicamentosService: MedicamentosService,
    private indicacionService: IndicacionService,
    private recetasService: RecetasService,
    private pacienteService: PacienteService,
  ) {}

  @ViewChild('stepper') stepper!: MatStepper;

  esMedico = true;
  pacientes: Paciente[] = [];

  cambiarRol() {
    this.esMedico = !this.esMedico;
  }

  user: any;

  paciente!: Paciente;

  atencion!: AtencionDashboard;

  sinConsultaActiva = false;

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.cargarIndicaciones();

    // Exploración → Receta
    this.exploracionForm.get('diagnostico')?.valueChanges.subscribe((valor) => {
      this.recetaForm.patchValue(
        {
          diagnostico: valor ?? '',
        },
        { emitEvent: false },
      );
    });

    // Receta → Exploración
    this.recetaForm.get('diagnostico')?.valueChanges.subscribe((valor) => {
      this.exploracionForm.patchValue(
        {
          diagnostico: valor ?? '',
        },
        { emitEvent: false },
      );
    });

    const navigation = history.state;

    if (navigation.paciente && navigation.atencion?.id) {
      this.paciente = navigation.paciente;

      const idAtencion = navigation.atencion.id;

      // console.log('ID de atención recibida:', idAtencion);
      // console.log('Consultando atención actual al backend...');

      this.atencionService.obtenerAtencion(idAtencion).subscribe({
        next: (atencionActual) => {
          // console.log('Atención actual desde backend:', atencionActual);

          this.atencion = atencionActual;

          if (!atencionActual.paciente?.id) {
            // console.error('La atención no contiene un paciente válido.');

            Swal.fire({
              icon: 'error',
              title: 'Paciente no encontrado',
              text: 'No fue posible obtener la información del paciente.',
              showConfirmButton: false,
              timer: 2000,
            });

            this.sinConsultaActiva = true;
            return;
          }

          this.pacienteService.obtenerPaciente(atencionActual.paciente.id).subscribe({
            next: (pacienteCompleto) => {
              this.paciente = pacienteCompleto;
              this.cargarHistorialSignosVitales();
              this.cargarArchivosAtencion();
              this.avisarHistoriaClinicaPendiente();
            },
            error: (error) => {
              this.paciente = atencionActual.paciente!;
              this.cargarHistorialSignosVitales();
              this.cargarArchivosAtencion();
              this.avisarHistoriaClinicaPendiente();
            },
          });

          if (this.atencion.estado === 'EN_ATENCION') {
            this.iniciarTemporizadorConsulta();
          }

          // Recuperar receta existente
          if (atencionActual.receta) {
            this.idReceta = atencionActual.receta.id;
            this.folioReceta = atencionActual.receta.folio;

            // console.log('Receta existente encontrada');
            // console.log('ID receta:', this.idReceta);
            // console.log('Folio receta:', this.folioReceta);

            this.cargarRecetaExistente(this.idReceta);
          } else {
            this.idReceta = null;
            this.folioReceta = '';

            // console.log('La atención todavía no tiene receta');
          }

          this.exploracionForm.patchValue({
            padecimiento: atencionActual.padecimiento ?? '',
            exploracionFisica: atencionActual.hallazgos ?? '',
            diagnostico: atencionActual.impresion ?? '',
          });

          this.recetaForm.patchValue({
            diagnostico: atencionActual.impresion ?? '',
          });

          // console.log('Padecimiento:', atencionActual.padecimiento);
          // console.log('Hallazgos:', atencionActual.hallazgos);
          // console.log('Impresión:', atencionActual.impresion);

          // console.log('Estado actual:', this.atencion.estado);
          // console.log('Hora fin:', this.atencion.horaFin);
        },

        error: (error) => {
          // console.error('Error al obtener la atención:', error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo cargar la atención',
            text: 'No fue posible obtener la información actual de la consulta.',
            showConfirmButton: false,
            timer: 2000,
          });

          this.sinConsultaActiva = true;
        },
      });
    } else {
      // console.log('No se recibió una atención activa');
      this.sinConsultaActiva = true;
    }

    const motivo = this.recetaForm.get('motivoProximaCita')!;
    motivo.disable({ emitEvent: false });

    this.recetaForm.get('proximaCita')?.valueChanges.subscribe((fecha) => {
      if (fecha) {
        motivo.enable({ emitEvent: false });
      } else {
        motivo.setValue('', { emitEvent: false });
        motivo.disable({ emitEvent: false });
      }
    });
  }

  limpiarProximaCita(): void {
    this.recetaForm.get('proximaCita')?.setValue(null);
  }

  // yyyy-MM-dd en hora local (toISOString podría correr el día por la zona horaria)
  private formatearFechaISO(fecha: Date | null): string | null {
    if (!fecha) return null;
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private parsearFechaISO(valor?: string | null): Date | null {
    if (!valor) return null;
    const [y, m, d] = valor.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  // Objeto listo para mandarlo al backend cuando existan los endpoints
  get planSeguimiento() {
    const v = this.recetaForm.getRawValue();
    return {
      proximaCita: this.formatearFechaISO(v.proximaCita),
      motivoProximaCita: v.motivoProximaCita?.trim() || null,
      metas: v.metas?.trim() || null,
      objetivos: v.objetivos?.trim() || null,
    };
  }

  // ! NUEVO PACIENTE REGISTRAR HISTORIA CLINICA

  get historiaClinicaIncompleta(): boolean {
    const expediente: any = this.paciente?.expediente;
    if (!expediente) return false; // aún no cargó, no avisar

    return CAMPOS_EXPEDIENTE.some((campo) => {
      const valor = String(expediente[campo] ?? '').trim();
      return !valor || valor === VALOR_EXPEDIENTE_VACIO;
    });
  }

  get esPrimeraConsulta(): boolean {
    const expediente: any = this.paciente?.expediente;
    if (!expediente) return false;

    return CAMPOS_EXPEDIENTE.every((campo) => {
      const valor = String(expediente[campo] ?? '').trim();
      return !valor || valor === VALOR_EXPEDIENTE_VACIO;
    });
  }

  private avisoHistoriaMostrado = false;

  private avisarHistoriaClinicaPendiente(): void {
    if (this.avisoHistoriaMostrado || !this.consultaActiva || !this.historiaClinicaIncompleta) {
      return;
    }

    this.avisoHistoriaMostrado = true;

    const primera = this.esPrimeraConsulta;

    Swal.fire({
      icon: 'info',
      title: primera ? 'Primera consulta del paciente' : 'Historia clínica incompleta',
      text: primera
        ? 'Es la primera consulta de este paciente. Registra su historia clínica antes de continuar.'
        : 'La historia clínica de este paciente está incompleta. Actualízala antes de continuar.',
      showCancelButton: true,
      confirmButtonText: 'Completar historia clínica',
      cancelButtonText: 'Más tarde',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((resultado) => {
      if (resultado.isConfirmed) {
        this.abrirHistoriaClinica();
      }
    });
  }

  private cargarRecetaExistente(idReceta: number): void {
    // console.log('Obteniendo receta:', idReceta);

    this.recetasService.obtenerReceta(idReceta).subscribe({
      next: (receta) => {
        // console.log('====================================');
        // console.log('RECETA EXISTENTE');
        // console.log('====================================');
        // console.log('Receta:', receta);
        // console.log('ID:', receta.id);
        // console.log('Folio:', receta.folio);
        // console.log('Impresión:', receta.impresion);
        // console.log('Estudios:', receta.estudios);
        // console.log('Medicamentos:', receta.medicamentos);

        // --------------------------------
        // DATOS PRINCIPALES DE LA RECETA
        // --------------------------------

        this.idReceta = receta.id;
        this.folioReceta = receta.folio;

        // --------------------------------
        // DIAGNÓSTICO
        // --------------------------------

        this.recetaForm.patchValue({
          diagnostico: receta.impresion ?? '',
        });

        this.recetaForm.patchValue({
          proximaCita: this.parsearFechaISO(receta.proximaCita),
          motivoProximaCita: receta.motivoProximaConsulta ?? receta.motivoProximaCita ?? '',
          metas: receta.metas ?? '',
          objetivos: receta.objetivos ?? '',
        });

        // --------------------------------
        // MEDICAMENTOS
        // --------------------------------

        this.medicamentosAgregados = receta.medicamentos ?? [];

        // --------------------------------
        // ESTUDIOS
        // --------------------------------

        this.estudiosAgregados = receta.estudios ?? [];

        // --------------------------------
        // INFORMACIÓN DE LA ATENCIÓN
        // --------------------------------

        this.exploracionForm.patchValue({
          padecimiento: receta.padecimiento ?? '',
          exploracionFisica: receta.hallazgos ?? '',
          diagnostico: receta.impresion ?? '',
        });

        // console.log('Datos de receta cargados correctamente');
        // console.log('Medicamentos cargados:', this.medicamentosAgregados);
        // console.log('Estudios cargados:', this.estudiosAgregados);
      },

      error: (error) => {
        // console.error('Error al obtener la receta:', error);

        Swal.fire({
          icon: 'error',
          title: 'No se pudo cargar la receta',
          text: error?.error?.message ?? 'No fue posible recuperar la información de la receta.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  columnasSignosVitales: string[] = ['fecha', 'peso', 'talla', 'fc', 'sato2', 'temperatura', 'ta'];

  cargarHistorialSignosVitales(): void {
    if (!this.paciente?.id) {
      return;
    }

    this.atencionService.obtenerAtenciones(this.paciente.id).subscribe({
      next: (atenciones) => {
        this.atenciones = atenciones;
      },

      error: (error) => {
        // console.error('Error al obtener el historial de signos vitales:', error);
      },
    });
  }

  fechaActual: Date = new Date();
  // CALCULAR EDAD
  obtenerEdad(paciente: Paciente): number {
    if (!paciente.fechaNacimiento) {
      return 0;
    }

    const fechaNacimiento = new Date(paciente.fechaNacimiento);

    const hoy = new Date();

    let edad = hoy.getFullYear() - fechaNacimiento.getFullYear();

    const mes = hoy.getMonth() - fechaNacimiento.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNacimiento.getDate())) {
      edad--;
    }

    return edad;
  }

  private _formBuilder = inject(FormBuilder);

  exploracionForm = this._formBuilder.group({
    padecimiento: [''],
    exploracionFisica: [''],
    diagnostico: [''],
  });

  // ====================================
  // HISTORIA CLÍNICA (antecedentes del paciente)
  // ====================================

  // readonly gruposHistoria: { titulo: string; campos: CampoHistoria[] }[] = [
  //   {
  //     titulo: 'Antecedentes personales no patológicos',
  //     campos: [
  //       { control: 'vacunas', etiqueta: 'Vacunas' },
  //       { control: 'viajesRecientes', etiqueta: 'Viajes recientes' },
  //       { control: 'lugarOrigen', etiqueta: 'Lugar de origen y de residencia' },
  //       { control: 'alimentacion', etiqueta: 'Alimentación' },
  //       { control: 'agua', etiqueta: 'Agua' },
  //       {
  //         control: 'vivienda',
  //         etiqueta: 'Vivienda',
  //         placeholder: 'Tipo de vivienda (renta, propia, prestada) y descripción general',
  //       },
  //     ],
  //   },
  //   {
  //     titulo: 'Antecedentes personales patológicos',
  //     campos: [
  //       { control: 'alergias', etiqueta: 'Alergias' },
  //       { control: 'transfucionesSangre', etiqueta: 'Transfusiones de sangre' },
  //       { control: 'cirugiasPrevias', etiqueta: 'Cirugías previas' },
  //       { control: 'traumasFracturasPrev', etiqueta: 'Traumas o fracturas previas' },
  //       { control: 'enfermedadesConcomitantes', etiqueta: 'Enfermedades concomitantes' },
  //       { control: 'diabetes', etiqueta: 'Diabetes' },
  //       { control: 'hipertension', etiqueta: 'Hipertensión' },
  //       { control: 'asma', etiqueta: 'Asma' },
  //       { control: 'epoc', etiqueta: 'Enfermedad pulmonar obstructiva crónica' },
  //       { control: 'antecedenteCancer', etiqueta: 'Antecedentes de cáncer en familia' },
  //       { control: 'antecedentesTuberculosis', etiqueta: 'Antecedentes de tuberculosis' },
  //     ],
  //   },
  // ];

  // private readonly camposHistoria: CampoHistoria[] = this.gruposHistoria.flatMap((g) => g.campos);

  // historiaClinicaForm = new FormGroup(
  //   Object.fromEntries(
  //     this.camposHistoria.map((c) => [
  //       c.control,
  //       new FormControl('', { nonNullable: true, validators: Validators.required }),
  //     ]),
  //   ) as Record<string, FormControl<string>>,
  // );

  guardandoHistoria = false;

  // private cargarHistoriaClinica(): void {
  //   const expediente: any = this.paciente?.expediente;

  //   if (!expediente) {
  //     return;
  //   }

  //   const valores: Record<string, string> = {};

  //   for (const campo of this.camposHistoria) {
  //     const valor = expediente[campo.control] ?? '';
  //     valores[campo.control] = valor === VALOR_EXPEDIENTE_VACIO ? '' : valor;
  //   }

  //   this.historiaClinicaForm.patchValue(valores);
  // }

  resumenAbierto = true;

  guardarHistoriaClinica(): void {
    if (!this.paciente?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró al paciente de la consulta.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    if (!this.consultaActiva) {
      Swal.fire({
        icon: 'warning',
        title: 'Consulta no disponible',
        text: 'La consulta ya no se encuentra activa.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const expediente: Record<string, string> = {};

    this.guardandoHistoria = true;

    this.pacienteService.actualizarPaciente(this.paciente.id, { expediente }).subscribe({
      next: () => {
        this.guardandoHistoria = false;

        this.paciente = {
          ...this.paciente,
          expediente: { ...(this.paciente.expediente as any), ...expediente },
        } as Paciente;

        this.stepper.next();

        Swal.fire({
          icon: 'success',
          title: 'Historia clínica guardada',
          text: 'Los antecedentes del paciente se guardaron correctamente.',
          showConfirmButton: false,
          timer: 2000,
        });
      },

      error: (error) => {
        this.guardandoHistoria = false;

        Swal.fire({
          icon: 'error',
          title: 'No se pudo guardar',
          text:
            error?.error?.message?.join?.(', ') ??
            error?.error?.message ??
            'Ocurrió un error al guardar la historia clínica.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  guardarExploracion(): void {
    // =====================================================
    // VALIDAR ID DE LA ATENCIÓN
    // =====================================================

    if (!this.atencion?.id) {
      // console.error('No se puede guardar la exploración: no existe el ID de la atención.');

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró la atención actual.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    // =====================================================
    // VALIDAR QUE LA CONSULTA SIGA ACTIVA
    // =====================================================

    if (!this.consultaActiva) {
      Swal.fire({
        icon: 'warning',
        title: 'Consulta no disponible',
        text: 'La consulta ya no se encuentra activa.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    // =====================================================
    // OBTENER DATOS DEL FORMULARIO
    // =====================================================

    const valores = this.exploracionForm.getRawValue();

    const diagnostico = valores.diagnostico?.trim() ?? '';

    const datos = {
      padecimiento: valores.padecimiento?.trim() ?? '',
      hallazgos: valores.exploracionFisica?.trim() ?? '',
      impresion: diagnostico,
    };

    // =====================================================
    // SINCRONIZAR DIAGNÓSTICO CON RECETA
    // =====================================================

    this.recetaForm.patchValue({
      diagnostico: diagnostico,
    });

    // console.log('====================================');
    // console.log('ACTUALIZANDO EXPLORACIÓN CLÍNICA');
    // console.log('ID atención:', this.atencion.id);
    // console.log('Datos enviados:', datos);
    // console.log('====================================');

    // =====================================================
    // ACTUALIZAR CONSULTA
    // =====================================================

    this.atencionService.actualizarConsulta(this.atencion.id, datos).subscribe({
      next: (respuesta) => {
        // console.log('Consulta actualizada correctamente:', respuesta);

        // ================================================
        // ACTUALIZAR ATENCIÓN LOCAL
        // ================================================

        if (respuesta?.data) {
          this.atencion = {
            ...this.atencion,
            ...respuesta.data,
          };
        }

        // ================================================
        // AVANZAR AL SIGUIENTE PASO
        // ANÁLISIS CLÍNICO
        // ================================================

        this.stepper.next();
      },

      error: (error) => {
        // console.error('Error al actualizar la exploración clínica:', error);

        Swal.fire({
          icon: 'error',
          title: 'No se pudo guardar',
          text:
            error?.error?.message ??
            error?.error?.mensaje ??
            'Ocurrió un error al guardar la información de la consulta.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  recetaForm = this._formBuilder.group({
    diagnostico: [''],
    indicaciones: [''],
    observaciones: [''],
    estudios: [''],

    proximaCita: [null as Date | null],
    motivoProximaCita: [''],
    metas: [''],
    objetivos: [''],
  });

  analisisClinicoForm = this._formBuilder.group({});

  // =========================
  // MEDICAMENTOS AGREGADOS
  // =========================
  medicamentosAgregados: any[] = [];

  eliminarMedicamento(index: number) {
    const medicamento = this.medicamentosAgregados[index];

    if (!medicamento?.id) {
      // console.error('No se puede eliminar.');
      return;
    }

    Swal.fire({
      title: '¿Eliminar medicamento?',
      text: `¿Estás seguro de eliminar el medicamento?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) {
        return;
      }

      this.medicamentosService.eliminarMedicamento(medicamento.id).subscribe({
        next: () => {
          this.medicamentosAgregados.splice(index, 1);

          Swal.fire({
            icon: 'success',
            title: 'Medicamento eliminado',
            showConfirmButton: false,
            timer: 1500,
          });
        },
        error: (error) => {
          // console.error('Error al eliminar el medicamento:', error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar el medicamento.',
            showConfirmButton: false,
            timer: 1500,
          });
        },
      });
    });
  }

  editarMedicamento(index: number): void {
    const medicamento = this.medicamentosAgregados[index];

    this.abrirDialogMedicamento(medicamento, index);
  }

  abrirDialogMedicamento(medicamento?: any, index?: number) {
    const dialogRef = this.dialog.open(Medicamentos, {
      width: '500px',
      data: {
        medicamento: medicamento,
        index: index,
        idReceta: this.idReceta,
      },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (!resultado) {
        return;
      }

      if (index !== undefined) {
        this.medicamentosAgregados[index] = resultado;
      } else {
        // Si no existe índice → estamos agregando
        this.medicamentosAgregados.push(resultado);
      }
    });
  }

  // =========================
  // ANÁLISIS SOLICITADOS PARA SUBIR (PDF, PNG)
  // =========================

  consultaFinalizada = false;

  get consultaActiva(): boolean {
    return !this.sinConsultaActiva && this.atencion?.estado === 'EN_ATENCION';
  }

  analisisAgregados: AnalisisClinico[] = [];

  cargarArchivosAtencion(): void {
    if (!this.paciente?.id) {
      return;
    }

    this.archivosAtencionService.obtenerArchivosPorPaciente(this.paciente.id).subscribe({
      next: (archivos: any[]) => {
        this.analisisAgregados = archivos.map((archivo) => ({
          id: archivo.id,
          atencionId: archivo.atencionId,
          estudio: archivo.estudio ?? 'Análisis clínico',
          resumen: archivo.hallazgos ?? '',
          archivo: null,
          archivoNombre: archivo.nombreArchivo ?? `Archivo ${archivo.id}`,
          fecha: archivo.fechaRegistro ? new Date(archivo.fechaRegistro) : new Date(),
          // Solo son editables los de la consulta actual
          editable: archivo.atencionId === this.atencion?.id,
        }));
      },
      error: (error) => {
        // console.error('Error al cargar los archivos del paciente:', error);
      },
    });
  }

  abrirDialogAnalisis(analisis?: AnalisisClinico, index?: number): void {
    if (!this.consultaActiva) {
      return;
    }

    const dialogRef = this.dialog.open(AnalisisClinicoDialog, {
      width: '500px',
      data: {
        analisis,
        index,
        atencionId: this.atencion?.id,
      },
    });

    dialogRef.afterClosed().subscribe((resultado: AnalisisClinico) => {
      if (!resultado) {
        return;
      }

      // EDITAR
      if (index !== undefined && analisis?.id) {
        this.archivosAtencionService
          .actualizarArchivo(analisis.id, {
            hallazgos: resultado.resumen,
            archivo: resultado.archivo ?? undefined,
          })
          .subscribe({
            next: () => {
              this.cargarArchivosAtencion();
              Swal.fire({
                icon: 'success',
                title: 'Análisis actualizado',
                showConfirmButton: false,
                timer: 1500,
              });
            },
            error: (error) => {
              // console.error('Error al actualizar el análisis:', error);
              Swal.fire({
                icon: 'error',
                title: 'No se pudo actualizar',
                text: error?.error?.message ?? 'Ocurrió un error al actualizar el análisis.',
                showConfirmButton: false,
                timer: 2000,
              });
            },
          });
        return;
      }

      // AGREGAR
      if (!resultado.archivo || !this.atencion?.id) {
        return;
      }

      this.archivosAtencionService
        .subirArchivoAtencion(resultado.archivo, this.atencion.id, resultado.resumen)
        .subscribe({
          next: () => {
            this.cargarArchivosAtencion();
            Swal.fire({
              icon: 'success',
              title: 'Análisis agregado',
              showConfirmButton: false,
              timer: 1500,
            });
          },
          error: (error) => {
            // console.error('Error al subir el análisis:', error);
            Swal.fire({
              icon: 'error',
              title: 'No se pudo subir',
              text: error?.error?.message ?? 'Ocurrió un error al subir el archivo.',
              showConfirmButton: false,
              timer: 2000,
            });
          },
        });
    });
  }

  editarAnalisis(index: number): void {
    const analisis = this.analisisAgregados[index];

    if (!this.consultaActiva || !analisis?.editable) {
      return;
    }

    this.abrirDialogAnalisis(analisis, index);
  }

  eliminarAnalisis(index: number): void {
    const analisis = this.analisisAgregados[index];

    if (!this.consultaActiva || !analisis?.editable || !analisis.id) {
      return;
    }

    Swal.fire({
      title: '¿Eliminar análisis?',
      text: `¿Estás seguro de eliminar el análisis "${analisis.estudio}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) {
        return;
      }

      this.archivosAtencionService.eliminarArchivo(analisis.id!).subscribe({
        next: () => {
          this.analisisAgregados.splice(index, 1);
          Swal.fire({
            icon: 'success',
            title: 'Análisis eliminado',
            showConfirmButton: false,
            timer: 1500,
          });
        },
        error: (error) => {
          // console.error('Error al eliminar el análisis:', error);
          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar el análisis.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  get recetaAnterior(): any | null {
    const anteriores = this.atenciones
      .filter((a) => a.id !== this.atencion?.id && (a as any).receta)
      .sort((a, b) => new Date(b.horaLlegada).getTime() - new Date(a.horaLlegada).getTime());

    return anteriores.length > 0 ? (anteriores[0] as any).receta : null;
  }

  indicacionesAgregadas: any[] = [];

  indicacionesDisponibles: any[] = [];

  cargarIndicaciones(): void {
    this.indicacionService.obtenerIndicaciones().subscribe({
      next: (indicaciones) => {
        // console.log('Indicaciones obtenidas:', indicaciones);
        this.indicacionesAgregadas = indicaciones ?? [];
        this.construirIndicacionesDisponibles();
      },

      error: (error) => {
        // console.error('Error al obtener las indicaciones:', error);
        this.indicacionesAgregadas = [];
        this.construirIndicacionesDisponibles();
      },
    });
  }

  construirIndicacionesDisponibles(): void {
    const personalizadas = this.indicacionesAgregadas.map((indicacion) => ({
      id: indicacion.id,
      nombre: indicacion.titulo,
      texto: indicacion.descripcion,
      personalizada: true,
    }));

    const predeterminadas = this.indicacionesGuardadas.map((indicacion) => ({
      ...indicacion,
      personalizada: false,
    }));

    this.indicacionesDisponibles = [...personalizadas, ...predeterminadas];

    // console.log('Indicaciones disponibles:', this.indicacionesDisponibles);
  }

  indicacionesGuardadas = [
    {
      nombre: 'Hidratación',
      texto:
        'Mantener adecuada hidratación. Beber al menos 2 litros de agua al día, salvo contraindicación médica.',
    },
    {
      nombre: 'Reposo',
      texto: 'Guardar reposo relativo y evitar esfuerzos físicos importantes.',
    },
    {
      nombre: 'Alimentos',
      texto: 'Tomar el medicamento después de los alimentos para disminuir molestias gástricas.',
    },
    {
      nombre: 'Ayuno',
      texto: 'Tomar el medicamento en ayuno, 30 minutos antes del desayuno.',
    },
    {
      nombre: 'No alcohol',
      texto: 'Evitar el consumo de bebidas alcohólicas durante el tratamiento.',
    },
    {
      nombre: 'No automedicarse',
      texto: 'No automedicarse ni suspender el tratamiento sin indicación médica.',
    },
    {
      nombre: 'Completar tratamiento',
      texto: 'Completar el tratamiento aunque los síntomas desaparezcan antes.',
    },
    {
      nombre: 'Signos de alarma',
      texto:
        'Acudir de inmediato a valoración médica si presenta fiebre persistente, dificultad para respirar, sangrado abundante o empeoramiento de los síntomas.',
    },
    {
      nombre: 'Dieta blanda',
      texto: 'Seguir una dieta blanda, evitando alimentos irritantes, grasosos y picantes.',
    },
    {
      nombre: 'Evitar irritantes',
      texto: 'Evitar el consumo de café, refrescos, tabaco y alimentos condimentados.',
    },
    {
      nombre: 'Control de temperatura',
      texto: 'Vigilar la temperatura corporal y registrar si presenta fiebre.',
    },
    {
      nombre: 'Actividad física',
      texto: 'Evitar actividad física intensa hasta nueva valoración.',
    },
    {
      nombre: 'Curaciones',
      texto: 'Mantener la herida limpia y seca. Realizar curaciones conforme a las indicaciones.',
    },
    {
      nombre: 'Regresar a consulta',
      texto: 'Acudir a consulta de seguimiento en la fecha indicada.',
    },
    {
      nombre: 'Urgencias',
      texto:
        'Acudir al servicio de urgencias si presenta dolor intenso, dificultad para respirar, pérdida del estado de alerta o cualquier síntoma de alarma.',
    },
  ];

  estudiosGuardados = [
    {
      nombre: 'Biometría hemática',
      texto:
        'Biometría hemática completa para evaluar glóbulos rojos, glóbulos blancos, hemoglobina, hematocrito y plaquetas.',
    },
    {
      nombre: 'Química sanguínea',
      texto:
        'Química sanguínea para evaluar principalmente glucosa, función renal y otros parámetros metabólicos.',
    },
    {
      nombre: 'Glucosa en sangre',
      texto:
        'Determinación de glucosa en sangre para evaluar los niveles de azúcar y detectar alteraciones de la glucemia.',
    },
    {
      nombre: 'Examen general de orina',
      texto:
        'Examen general de orina para evaluar características físicas, químicas y microscópicas de la orina.',
    },
    {
      nombre: 'Perfil lipídico',
      texto:
        'Perfil lipídico para evaluar colesterol total, colesterol HDL, colesterol LDL y triglicéridos.',
    },
    {
      nombre: 'Pruebas de función hepática',
      texto:
        'Evaluación de la función hepática mediante pruebas como AST, ALT, fosfatasa alcalina, bilirrubina y otros parámetros.',
    },
    {
      nombre: 'Pruebas de función renal',
      texto:
        'Evaluación de la función renal mediante creatinina, urea y otros parámetros relacionados con el funcionamiento de los riñones.',
    },
    {
      nombre: 'Hemoglobina glucosilada (HbA1c)',
      texto:
        'Determinación de hemoglobina glucosilada para evaluar el control promedio de la glucosa en sangre durante los últimos meses.',
    },
    {
      nombre: 'Perfil tiroideo',
      texto:
        'Evaluación de la función tiroidea mediante pruebas como TSH y, cuando está indicado, T4 libre.',
    },
    {
      nombre: 'Electrolitos séricos',
      texto:
        'Determinación de electrolitos como sodio, potasio, cloro y bicarbonato para evaluar el equilibrio hidroelectrolítico.',
    },
    {
      nombre: 'Tiempo de protrombina (TP)',
      texto:
        'Prueba de coagulación utilizada para evaluar la vía extrínseca de la coagulación y el tiempo necesario para formar un coágulo.',
    },
    {
      nombre: 'Tiempo de tromboplastina parcial (TTPa)',
      texto: 'Prueba de coagulación utilizada para evaluar la vía intrínseca de la coagulación.',
    },
    {
      nombre: 'Grupo sanguíneo y Rh',
      texto: 'Determinación del grupo sanguíneo ABO y del factor Rh.',
    },
    {
      nombre: 'Prueba de embarazo',
      texto: 'Determinación de gonadotropina coriónica humana (hCG) para detectar embarazo.',
    },
    {
      nombre: 'Proteína C reactiva (PCR)',
      texto:
        'Prueba utilizada como apoyo para evaluar la presencia y evolución de procesos inflamatorios.',
    },
    {
      nombre: 'Velocidad de sedimentación globular (VSG)',
      texto: 'Prueba utilizada como apoyo en la evaluación de procesos inflamatorios.',
    },
    {
      nombre: 'Coprológico',
      texto:
        'Examen de materia fecal para evaluar características macroscópicas y microscópicas y detectar alteraciones gastrointestinales.',
    },
    {
      nombre: 'Examen parasitológico de heces',
      texto:
        'Estudio de materia fecal para detectar la presencia de parásitos intestinales y sus formas evolutivas.',
    },
    {
      nombre: 'Cultivo de orina',
      texto:
        'Estudio microbiológico de orina para identificar microorganismos causantes de infección urinaria y orientar el tratamiento.',
    },
    {
      nombre: 'Cultivo de exudado faríngeo',
      texto:
        'Estudio microbiológico de una muestra de garganta para identificar microorganismos causantes de infección.',
    },
    {
      nombre: 'Prueba de VIH',
      texto:
        'Prueba de detección de infección por el virus de la inmunodeficiencia humana (VIH), realizada con consentimiento y orientación médica.',
    },
  ];

  seleccionarIndicaciones(indicaciones: any[]) {
    if (!indicaciones || indicaciones.length === 0) {
      this.recetaForm.get('indicaciones')?.setValue('');
      return;
    }

    const texto = indicaciones.map((i) => `• ${i.texto}`).join('\n');

    this.recetaForm.get('indicaciones')?.setValue(texto);
  }

  seleccionarEstudios(indicaciones: any[]) {
    if (!indicaciones || indicaciones.length === 0) {
      this.recetaForm.get('estudios')?.setValue('');
      return;
    }

    const texto = indicaciones.map((i) => `• ${i.texto}`).join('\n');

    this.recetaForm.get('estudios')?.setValue(texto);
  }

  agregarVinyeta(event: Event, campo: string) {
    event.preventDefault();

    const control = this.recetaForm.get(campo);

    if (!control) return;

    const texto = control?.value || '';

    control?.setValue(texto + '\n• ');
  }

  iniciarVinyetas(campo: string) {
    const control = this.recetaForm.get(campo);

    if (!control) return;

    const texto = control.value || '';

    // Solo agrega la primera viñeta si está vacío
    if (!texto.trim()) {
      control.setValue('• ');
    }
  }

  // ====================================
  // ESTUDIOS SOLICITADOS EN LA RECETA
  // ====================================
  estudiosFormGrup = this._formBuilder.group({});
  estudiosAgregados: any[] = [];

  editarEstudio(index: number): void {
    const estudio = this.estudiosAgregados[index];

    this.abrirDialogEstudios(estudio, index);
  }

  abrirDialogEstudios(estudio?: any, index?: number): void {
    const dialogRef = this.dialog.open(EstudiosSolicitadosDialog, {
      width: '500px',
      data: {
        atencionId: this.atencion?.id,

        estudio: estudio,

        index: index,
      },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (!resultado) {
        return;
      }

      if (index !== undefined) {
        this.estudiosAgregados[index] = resultado;
      } else {
        this.estudiosAgregados.push(resultado);
      }
    });
  }

  eliminarEstudio(index: number) {
    const estudio = this.estudiosAgregados[index];

    if (!estudio?.id) {
      // console.error('No se puede eliminar el estudio solicitado.');
      return;
    }

    Swal.fire({
      title: '¿Eliminar estudio',
      text: `¿Estás seguro de eliminar el estudio "${estudio.nombre}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) {
        return;
      }

      this.estudiosService.eliminarEstudio(estudio.id).subscribe({
        next: () => {
          this.estudiosAgregados.splice(index, 1);
          Swal.fire({
            icon: 'success',
            title: 'Estudio eliminado',
            showConfirmButton: false,
            timer: 1500,
          });

          // this.cargar();
        },
        error: (error) => {
          // console.error('Error al eliminar el estudio:', error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar el estudio.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  clinica = {
    nombre: 'Clínica NovaMed',
    direccion: 'Av. Principal #123, Oaxaca, Oaxaca',
    telefono: '951 123 4567',
  };

  folioReceta = '';
  idReceta: number | null = null;
  noExpediente = 'JKLFSD98324';
  creandoReceta = false;

  crearReceta(): void {
    if (!this.atencion?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró la atención actual.',
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }

    if (this.atencion.estado !== 'EN_ATENCION') {
      Swal.fire({
        icon: 'warning',
        title: 'Consulta no disponible',
        text: `La atención se encuentra en estado ${this.atencion.estado}.`,
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }

    // Si ya existe una receta, no crear otra.
    if (this.idReceta) {
      return;
    }

    // Evitar solicitudes duplicadas.
    if (this.creandoReceta) {
      return;
    }

    this.creandoReceta = true;

    this.recetasService.crearReceta(this.atencion.id).subscribe({
      next: (respuesta: any) => {
        const receta = respuesta?.data;

        if (!receta?.id) {
          this.creandoReceta = false;

          Swal.fire({
            icon: 'error',
            title: 'Respuesta incompleta',
            text: 'El servidor respondió, pero no devolvió el identificador de la receta.',
            showConfirmButton: false,
            timer: 2000,
          });

          return;
        }

        this.idReceta = receta.id;
        this.folioReceta = receta.folio ?? '';

        this.creandoReceta = false;

        // Conservar la receta en la atención local.
        this.atencion = {
          ...this.atencion,
          receta,
        };

        // Swal.fire({
        //   icon: 'success',
        //   title: 'Receta preparada',
        //   text: 'Ya puedes generar el PDF de la receta.',
        //   confirmButtonColor: '#24575A',
        // });
      },

      error: (error) => {
        this.creandoReceta = false;

        Swal.fire({
          icon: 'error',
          title: 'No se pudo crear la receta',
          text:
            error?.error?.message?.toString?.() ??
            error?.error?.mensaje ??
            'Ocurrió un error al crear la receta.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  private readonly PASO_RECETAS = 2;

  cambioPaso(event: StepperSelectionEvent): void {
    if (event.selectedIndex === this.PASO_RECETAS && !this.idReceta) {
      this.crearReceta();
    }
  }

  async generarPDF(): Promise<void> {
    if (!this.atencion?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró la atención actual.',
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }

    if (!this.idReceta) {
      Swal.fire({
        icon: 'warning',
        title: 'Receta no disponible',
        text: 'Primero debe generarse la receta antes de finalizar la consulta.',
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }

    if (this.atencion.estado !== 'EN_ATENCION') {
      Swal.fire({
        icon: 'warning',
        title: 'Consulta no disponible',
        text: `La atención se encuentra en estado ${this.atencion.estado}.`,
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const resultado = await Swal.fire({
      title: '¿Generar receta?',
      text: 'Al generar la receta se finalizará la consulta del paciente.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, generar y finalizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#777',
      reverseButtons: true,
    });

    if (!resultado.isConfirmed) {
      return;
    }

    // ==========================================
    // CONVERTIR ESTUDIOS A TEXTO
    // ==========================================

    const estudiosTexto = this.estudiosAgregados
      .map((estudio) => {
        const nombre = estudio.nombre ?? '';
        const indicaciones = estudio.indicaciones ?? '';

        if (indicaciones.trim()) {
          return `• ${nombre}\n  ${indicaciones}`;
        }

        return `• ${nombre}`;
      })
      .join('\n');

    const plan = this.planSeguimiento;

    try {
      await firstValueFrom(
        this.recetasService.actualizarPlanSeguimiento({
          id: this.idReceta!,
          proximaCita: plan.proximaCita,
          motivoProximaConsulta: plan.motivoProximaCita,
          metas: plan.metas,
          objetivos: plan.objetivos,
        }),
      );
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo guardar el plan de seguimiento',
        text:
          error?.error?.message?.toString?.() ??
          error?.error?.mensaje ??
          'No fue posible guardar la próxima cita. La consulta continúa activa.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    const receta = {
      ...this.recetaForm.getRawValue(),
      estudios: estudiosTexto,
      proximaCita: this.recetaForm.get('proximaCita')?.value
        ? this.recetaForm.get('proximaCita')!.value!.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })
        : '',
      motivoProximaCita: plan.motivoProximaCita ?? '',
      metas: plan.metas ?? '',
      objetivos: plan.objetivos ?? '',
    };

    // console.log('ESTUDIOS QUE SE ENVIARÁN AL PDF:', this.estudiosAgregados);
    // console.log('TEXTO DE ESTUDIOS:', estudiosTexto);

    // ==========================================
    // GENERAR PDF
    // ==========================================

    try {
      await this.recetaPdf.generarReceta({
        paciente: this.paciente,
        signosVitales: this.ultimoSignosVitales,
        medico: this.atencion.medico ?? this.user, // médico real asignado a la atención
        medicamentos: this.medicamentosAgregados,
        receta: receta,
        folio: this.folioReceta,
        numeroExpediente: this.paciente.numeroExpedienteMedico,
      });

      // console.log('PDF generado correctamente.');
    } catch (error) {
      // console.error('Error al generar el PDF:', error);

      Swal.fire({
        icon: 'error',
        title: 'No se pudo generar la receta',
        text: 'La consulta no será finalizada porque ocurrió un error al generar el PDF.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    // ==========================================
    // FINALIZAR ATENCIÓN
    // ==========================================

    this.atencionService.finalizarAtencion(this.atencion.id).subscribe({
      next: (respuesta) => {
        // console.log('====================================');
        // console.log('ATENCIÓN FINALIZADA DESPUÉS DE GENERAR RECETA');
        // console.log('ID:', this.atencion.id);
        // console.log('RESPUESTA:', respuesta);
        // console.log('====================================');

        // Actualizar atención con la respuesta del backend
        this.atencion = {
          ...this.atencion,
          ...respuesta.data,
        };

        // Persistir estado para el dashboard
        this.estadoAtencionService.setEstado(this.atencion.id, 'FINALIZADA');

        Swal.fire({
          icon: 'success',
          title: 'Consulta finalizada',
          text: 'La receta se generó y la consulta se finalizó correctamente.',
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          this.router.navigate(['/inicio']);
        });
      },

      error: (error) => {
        // console.error('Error al finalizar atención:', error);

        Swal.fire({
          icon: 'error',
          title: 'Receta generada',
          text: 'La receta se generó correctamente, pero no se pudo finalizar la consulta.',
          showConfirmButton: false,
          timer: 15000,
        });
      },
    });
  }

  finalizarConsulta(): void {
    if (!this.atencion?.id) {
      // console.error('No se puede finalizar la consulta: no existe el ID de la atención.');

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró la atención que se está consultando.',
        showConfirmButton: false,
        timer: 1500,
      });

      return;
    }

    Swal.fire({
      title: '¿Finalizar consulta?',
      text: 'La atención del paciente será marcada como finalizada.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, finalizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#777',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) {
        return;
      }

      this.atencionService.finalizarAtencion(this.atencion.id!).subscribe({
        next: (respuesta) => {
          this.atencion = { ...this.atencion, ...respuesta.data };
          this.estadoAtencionService.setEstado(this.atencion.id!, 'FINALIZADA');
          // console.log('====================================');
          // console.log('ATENCIÓN FINALIZADA');
          // console.log('ID:', this.atencion.id);
          // console.log('RESPUESTA:', respuesta);
          // console.log('====================================');

          // Persistimos el estado para que Inicio lo lea al volver
          this.estadoAtencionService.setEstado(this.atencion.id!, 'FINALIZADA');

          Swal.fire({
            icon: 'success',
            title: 'Consulta finalizada',
            text: 'La atención se finalizó correctamente.',
            showConfirmButton: false,
            timer: 1500,
          }).then(() => {
            this.router.navigate(['/inicio']);
          });
        },

        error: (error) => {
          // console.error('Error al finalizar atención:', error);
          Swal.fire({
            icon: 'error',
            title: 'No se pudo finalizar',
            text: 'Ocurrió un error al finalizar la atención.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  atenciones: Atencion[] = [];

  get signosVitales(): SignosVitales[] {
    return this.atenciones

      .filter((atencion) => atencion.signosVitales != null)

      .map((atencion) => atencion.signosVitales!)

      .sort((a, b) => new Date(a.fechaRegistro).getTime() - new Date(b.fechaRegistro).getTime());
  }

  get ultimoSignosVitales(): SignosVitales | null {
    const registros = this.signosVitales;
    return registros.length > 0 ? registros[registros.length - 1] : null;
  }

  get ultimaObservacionSignosVitales(): string | null {
    const ultimo = this.ultimoSignosVitales;

    if (!ultimo) {
      return null;
    }

    const observacion = ultimo.observaciones?.trim();

    // El último registro no tiene observación.
    // No se busca una observación anterior.
    if (!observacion) {
      return null;
    }

    // Ignorar el mensaje automático generado durante el triage.
    if (observacion.toLowerCase() === 'signos vitales registrados durante el triage') {
      return null;
    }

    return observacion;
  }

  // * TEMPORIZADOR DE CONSULTA

  readonly DURACION_CONSULTA = 30 * 60; // * minutios x segundos

  tiempoRestante = this.DURACION_CONSULTA;
  progresoConsulta = 100;
  temporizadorActivo = false;
  tiempoCumplido = false;
  alertaTiempoMostrada = false;

  private temporizador?: ReturnType<typeof setInterval>;

  private iniciarTemporizadorConsulta(): void {
    // Limpiar un temporizador anterior por seguridad
    this.detenerTemporizador();

    if (!this.atencion?.horaInicio) {
      // console.warn('No existe horaInicio para iniciar el temporizador.');
      return;
    }

    this.temporizadorActivo = true;
    this.tiempoCumplido = false;

    const horaInicio = new Date(this.atencion.horaInicio).getTime();

    this.actualizarTemporizador(horaInicio);

    this.temporizador = setInterval(() => {
      this.actualizarTemporizador(horaInicio);
    }, 1000);
  }

  private actualizarTemporizador(horaInicio: number): void {
    const ahora = Date.now();

    const segundosTranscurridos = Math.floor((ahora - horaInicio) / 1000);

    const segundosRestantes = Math.max(this.DURACION_CONSULTA - segundosTranscurridos, 0);

    this.tiempoRestante = segundosRestantes;

    this.progresoConsulta = (segundosRestantes / this.DURACION_CONSULTA) * 100;

    if (segundosRestantes <= 0) {
      this.tiempoCumplido = true;

      if (!this.alertaTiempoMostrada) {
        this.alertaTiempoMostrada = true;
        this.reproducirAlerta();
      }

      this.detenerTemporizador();
    }
  }

  get tiempoRestanteFormato(): string {
    const minutos = Math.floor(this.tiempoRestante / 60);
    const segundos = this.tiempoRestante % 60;

    return `${minutos}:${segundos.toString().padStart(2, '0')}`;
  }

  private detenerTemporizador(): void {
    if (this.temporizador) {
      clearInterval(this.temporizador);
      this.temporizador = undefined;
    }

    this.temporizadorActivo = false;
  }

  private reproducirAlerta(): void {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;

      const contexto = new AudioContext();

      const oscilador = contexto.createOscillator();
      const ganancia = contexto.createGain();

      oscilador.type = 'sine';
      oscilador.frequency.setValueAtTime(880, contexto.currentTime);

      ganancia.gain.setValueAtTime(0.25, contexto.currentTime);
      ganancia.gain.exponentialRampToValueAtTime(0.01, contexto.currentTime + 0.8);

      oscilador.connect(ganancia);
      ganancia.connect(contexto.destination);

      oscilador.start();

      oscilador.stop(contexto.currentTime + 0.8);
    } catch (error) {
      // console.warn('No fue posible reproducir la alerta sonora.', error);
    }
  }

  ngOnDestroy(): void {
    this.detenerTemporizador();
  }

  abrirResumenEstudios(paciente: Paciente): void {
    this.dialog.open(ResumenEstudiosDialog, {
      width: '700px',
      maxWidth: '95vw',
      height: '80vh',
      data: paciente,
    });
  }

  abrirResumenReceta(paciente: Paciente): void {
    this.dialog.open(ResumenRecetaDialog, {
      width: '700px',
      maxWidth: '95vw',
      height: '80vh',
      data: paciente,
    });
  }

  abrirHistorialSignosVitales(): void {
    this.dialog.open(HistorialSignosVitalesDialog, {
      width: '900px',
      maxWidth: '95vw',
      height: '80vh',
      data: {
        paciente: this.paciente,
        signosVitales: [...this.signosVitales].reverse(), // el más reciente primero
      },
    });
  }

  abrirHistoriaClinica(): void {
    if (!this.paciente?.id) {
      return;
    }

    const dialogRef = this.dialog.open(HistoriaClinicaDialog, {
      width: '900px',
      maxWidth: '95vw',
      height: '85vh',
      disableClose: true, // evita perder cambios por un clic fuera o Esc
      data: { paciente: this.paciente, editable: this.consultaActiva },
    });

    dialogRef.afterClosed().subscribe((expediente?: Record<string, string>) => {
      if (!expediente) {
        return;
      }

      // El encabezado (alergias) y el PDF leen paciente.expediente
      this.paciente = {
        ...this.paciente,
        expediente: { ...(this.paciente.expediente as any), ...expediente },
      } as Paciente;
    });
  }

  abrirGragicaSignosVitales() {
    // this.dialogRef.close();

    this.dialog.open(GraficasSignosVitales, {
      width: '95vw',
      height: '90vh',
      maxWidth: '95vw',
      data: this.paciente,
    });
  }
}
